/**
 * BigData Insights Service - Gestion centralisée des insights
 * Optimisé pour des millions d'insights avec tri intelligent
 */

import { supabase as supabaseClient } from '../lib/supabase';
import { debugLog } from '../utils/debug-logger';
import logger from '../utils/logger';

export type InsightType = 'pattern' | 'anomaly' | 'correlation' | 'trend' | 'statistical' | 'business' | 'quality';
export type InsightCategory = 'critical' | 'high_value' | 'medium_value' | 'low_value' | 'warning' | 'info';
export type InsightSource = 'ai_navigator' | 'ai_chat' | 'auto_analysis' | 'user_query' | 'manual';

export interface BigDataInsight {
  id: string;
  user_id: string;
  dataset_id: string;
  session_id?: string;
  
  // Classification
  type: InsightType;
  category: InsightCategory;
  confidence: number; // 0-1
  
  // Contenu
  title: string;
  description: string;
  details?: any;
  
  // Localisation
  row_start?: number;
  row_end?: number;
  page_number?: number;
  affected_columns?: string[];
  
  // Métriques
  impact_score?: number;
  relevance_score?: number;
  occurrence_count?: number;
  
  // SQL
  sql_query?: string;
  sql_result_count?: number;
  sql_execution_time_ms?: number;
  
  // Métadonnées
  discovered_by: InsightSource;
  discovered_at: string;
  tags?: string[];
  recommended_actions?: any[];
  
  // Statut
  is_bookmarked?: boolean;
  is_validated?: boolean;
  is_archived?: boolean;
  view_count?: number;
  bookmark_count?: number;
  
  created_at: string;
  updated_at: string;
}

export interface InsightFilters {
  types?: InsightType[];
  categories?: InsightCategory[];
  minConfidence?: number;
  onlyBookmarked?: boolean;
  search?: string;
  tags?: string[];
}

export interface InsightSortOptions {
  sortBy: 'discovered_at' | 'confidence' | 'impact_score' | 'importance' | 'relevance_score';
  sortOrder: 'ASC' | 'DESC';
}

export interface PaginatedInsights {
  insights: BigDataInsight[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

class BigDataInsightsService {
  /**
   * 🗺️ Mapper business_patterns vers BigDataInsight
   */
  private static mapBusinessPatternToInsight(pattern: any): BigDataInsight {
    return {
      id: pattern.id,
      user_id: pattern.user_id,
      dataset_id: pattern.dataset_id,
      session_id: pattern.session_id,
      type: pattern.pattern_type,
      category: this.estimateCategory(pattern),
      confidence: pattern.confidence_score,
      title: pattern.title,
      description: pattern.description,
      details: pattern.metadata?.details,
      row_start: pattern.row_start,
      row_end: pattern.row_end,
      page_number: pattern.page_number,
      affected_columns: pattern.affected_columns,
      impact_score: pattern.estimated_impact_eur ? pattern.estimated_impact_eur / 100000 : undefined,
      relevance_score: pattern.metadata?.relevance_score || 0.5,
      occurrence_count: pattern.occurrences_count,
      sql_query: pattern.metadata?.sql_query,
      sql_result_count: pattern.metadata?.sql_result_count,
      sql_execution_time_ms: pattern.metadata?.sql_execution_time_ms,
      discovered_by: pattern.discovered_by,
      discovered_at: pattern.discovered_at || pattern.created_at,
      tags: pattern.tags || [],
      recommended_actions: [],
      is_bookmarked: pattern.is_bookmarked,
      is_validated: pattern.is_validated_by_user,
      is_archived: !pattern.is_active,
      view_count: pattern.view_count || 0,
      bookmark_count: pattern.bookmark_count || 0,
      created_at: pattern.created_at,
      updated_at: pattern.updated_at
    };
  }

  /**
   * 🗺️ Estimer la catégorie depuis pattern
   */
  private static estimateCategory(pattern: any): InsightCategory {
    if (pattern.pattern_type === 'anomaly') return 'critical';
    if (pattern.impact_category === 'risk') return 'critical';
    if (pattern.confidence_score >= 0.9) return 'high_value';
    if (pattern.confidence_score >= 0.7) return 'medium_value';
    return 'low_value';
  }
  
  /**
   * 💾 Créer un nouvel insight (utilise business_patterns)
   */
  static async createInsight(insight: Omit<BigDataInsight, 'id' | 'created_at' | 'updated_at'>): Promise<BigDataInsight> {
    debugLog('[💡 InsightsService] Creating insight:', insight.title);
    
    const { data, error } = await supabaseClient
      .from('business_patterns')
      .insert({
        user_id: insight.user_id,
        dataset_id: insight.dataset_id,
        session_id: insight.session_id || null, // ✅ UUID de big_data_sessions (peut être null)
        pattern_type: insight.type,
        title: insight.title,
        description: insight.description,
        confidence_score: insight.confidence,
        estimated_impact_eur: insight.impact_score ? Math.round(insight.impact_score * 100000) : null,
        row_start: insight.row_start,
        row_end: insight.row_end,
        affected_columns: insight.affected_columns,
        discovered_by: insight.discovered_by,
        discovered_at: insight.discovered_at,
        tags: insight.tags || [],
        is_bookmarked: insight.is_bookmarked || false,
        is_validated_by_user: insight.is_validated || false,
        metadata: {
          sql_query: insight.sql_query,
          sql_result_count: insight.sql_result_count,
          sql_execution_time_ms: insight.sql_execution_time_ms,
          relevance_score: insight.relevance_score,
          details: insight.details
        },
        search_vector: null, // Will be auto-generated by trigger
      })
      .select()
      .single();

    if (error) {
      logger.error('[❌ InsightsService] Failed to create insight:', error);
      throw error;
    }

    debugLog('[✅ InsightsService] Insight created:', data.id);
    return this.mapBusinessPatternToInsight(data);
  }

  /**
   * 💾 Créer plusieurs insights en batch (optimisé)
   */
  static async createInsightsBatch(insights: Omit<BigDataInsight, 'id' | 'created_at' | 'updated_at'>[]): Promise<BigDataInsight[]> {
    if (insights.length === 0) return [];
    
    debugLog('[💡 InsightsService] Creating batch of insights:', insights.length);
    
    // ✅ Mapper InsightType vers business_patterns.pattern_type
    const typeToPatternType = (type: InsightType): string => {
      const mapping: { [key in InsightType]: string } = {
        'pattern': 'trend',
        'anomaly': 'anomaly',
        'correlation': 'correlation',
        'trend': 'trend',
        'statistical': 'trend',
        'business': 'opportunity',
        'quality': 'risk'
      };
      return mapping[type] || 'trend';
    };
    
    const { data, error } = await supabaseClient
      .from('business_patterns')
      .insert(insights.map(i => ({
        user_id: i.user_id,
        dataset_id: i.dataset_id,
        session_id: i.session_id || null,
        pattern_type: typeToPatternType(i.type),
        title: i.title,
        description: i.description,
        confidence_score: i.confidence,
        estimated_impact_eur: i.impact_score ? Math.round(i.impact_score * 100000) : null,
        
        // ✅ FIX: Colonnes racine SQL (pas metadata)
        sql_query: i.sql_query,
        sql_result_count: i.sql_result_count,
        sql_execution_time_ms: i.sql_execution_time_ms,
        discovered_by: i.discovered_by,
        discovered_at: i.discovered_at,
        relevance_score: i.relevance_score || 0.5,
        is_bookmarked: i.is_bookmarked || false,
        
        // Colonnes optionnelles
        row_start: i.row_start,
        row_end: i.row_end,
        affected_columns: i.affected_columns,
        
        // Tags
        tags: i.tags || [],
        is_validated_by_user: i.is_validated || false,
        
        // Metadata pour données extra
        metadata: {
          insight_type: i.type, // Type original pour référence
          details: i.details
        }
      })))
      .select();

    if (error) {
      debugLog('[❌ InsightsService] Failed to create batch:', error);
      throw error;
    }

    debugLog('[✅ InsightsService] Batch created:', data.length);
    return data.map(this.mapBusinessPatternToInsight);
  }

  /**
   * 📊 Récupérer les insights d'un dataset avec filtres et pagination
   */
  static async getDatasetInsights(
    datasetId: string,
    userId: string,
    filters: InsightFilters = {},
    sort: InsightSortOptions = { sortBy: 'discovered_at', sortOrder: 'DESC' },
    page: number = 0,
    pageSize: number = 50
  ): Promise<PaginatedInsights> {
    debugLog('[📊 InsightsService] Fetching insights for dataset:', datasetId);
    debugLog('[🔍 InsightsService] Filters:', filters);
    debugLog('[🔍 InsightsService] Sort:', sort);
    
    let query = supabaseClient
      .from('business_patterns')
      .select('*', { count: 'exact' })
      .eq('dataset_id', datasetId)
      .eq('user_id', userId)
      .eq('is_active', true);

    // Appliquer les filtres
    if (filters.types && filters.types.length > 0) {
      query = query.in('pattern_type', filters.types);
    }
    
    if (filters.categories && filters.categories.length > 0) {
      query = query.in('category', filters.categories);
    }
    
    if (filters.minConfidence !== undefined) {
      query = query.gte('confidence_score', filters.minConfidence);
    }
    
    if (filters.onlyBookmarked) {
      query = query.eq('is_bookmarked', true);
    }
    
    if (filters.search) {
      // Full-text search
      query = query.textSearch('search_vector', filters.search);
    }
    
    if (filters.tags && filters.tags.length > 0) {
      query = query.contains('tags', filters.tags);
    }

    // Tri intelligent
    if (sort.sortBy === 'importance') {
      query = query.order('estimated_impact_eur', { ascending: sort.sortOrder === 'ASC' })
                   .order('confidence_score', { ascending: sort.sortOrder === 'ASC' });
    } else if (sort.sortBy === 'confidence') {
      query = query.order('confidence_score', { ascending: sort.sortOrder === 'ASC' });
    } else if (sort.sortBy === 'impact_score') {
      query = query.order('estimated_impact_eur', { ascending: sort.sortOrder === 'ASC' });
    } else {
      query = query.order(sort.sortBy, { ascending: sort.sortOrder === 'ASC' });
    }

    // Pagination
    const start = page * pageSize;
    const end = start + pageSize - 1;
    query = query.range(start, end);

    const { data, error, count } = await query;

    if (error) {
      logger.error('[❌ InsightsService] Failed to fetch insights:', error);
      throw error;
    }

    const total = count || 0;
    const hasMore = (start + (data?.length || 0)) < total;

    debugLog('[✅ InsightsService] Fetched insights:', {
      count: data?.length || 0,
      total,
      page,
      hasMore
    });

    return {
      insights: (data || []).map(this.mapBusinessPatternToInsight.bind(this)),
      total,
      page,
      pageSize,
      hasMore
    };
  }

  /**
   * 🔍 Recherche full-text optimisée
   */
  static async searchInsights(
    datasetId: string,
    userId: string,
    searchTerm: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<BigDataInsight[]> {
    debugLog('[🔍 InsightsService] Searching insights:', searchTerm);
    
    const { data, error } = await supabaseClient
      .rpc('search_insights', {
        p_user_id: userId,
        p_dataset_id: datasetId,
        p_search_term: searchTerm,
        p_limit: limit,
        p_offset: offset
      });

    if (error) {
      logger.error('[❌ InsightsService] Search failed:', error);
      throw error;
    }

    debugLog('[✅ InsightsService] Search results:', data.length);
    return data;
  }

  /**
   * 📈 Statistiques d'un dataset
   */
  static async getDatasetStats(datasetId: string): Promise<any> {
    debugLog('[📈 InsightsService] Fetching stats for dataset:', datasetId);
    
    // ✅ Use RPC function instead of direct materialized view access (RLS-compliant)
    const { data, error } = await supabaseClient
      .rpc('get_bigdata_insights_stats', { p_dataset_id: datasetId });

    if (error && error.code !== 'PGRST116') {
      logger.warn('[⚠️ InsightsService] Stats not found (may need refresh):', error.message);
      return null;
    }

    debugLog('[✅ InsightsService] Stats:', data);
    return data;
  }

  /**
   * ⭐ Toggle bookmark
   */
  static async toggleBookmark(insightId: string, userId: string): Promise<void> {
    debugLog('[⭐ InsightsService] Toggling bookmark:', insightId);
    
    const { data: current, error: fetchError } = await supabaseClient
      .from('business_patterns')
      .select('is_bookmarked')
      .eq('id', insightId)
      .eq('user_id', userId)
      .single();

    if (fetchError) throw fetchError;

    const { error } = await supabaseClient
      .from('business_patterns')
      .update({ 
        is_bookmarked: !current.is_bookmarked,
        bookmark_count: current.is_bookmarked ? 0 : 1
      })
      .eq('id', insightId)
      .eq('user_id', userId);

    if (error) throw error;
    
    debugLog('[✅ InsightsService] Bookmark toggled');
  }

  /**
   * ✅ Toggle validation
   */
  static async toggleValidation(insightId: string, userId: string): Promise<void> {
    debugLog('[✅ InsightsService] Toggling validation:', insightId);
    
    const { data: current, error: fetchError } = await supabaseClient
      .from('business_patterns')
      .select('is_validated_by_user')
      .eq('id', insightId)
      .eq('user_id', userId)
      .single();

    if (fetchError) throw fetchError;

    const { error } = await supabaseClient
      .from('business_patterns')
      .update({ is_validated_by_user: !current.is_validated_by_user })
      .eq('id', insightId)
      .eq('user_id', userId);

    if (error) throw error;
    
    debugLog('[✅ InsightsService] Validation toggled');
  }

  /**
   * 🗑️ Archiver un insight
   */
  static async archiveInsight(insightId: string, userId: string): Promise<void> {
    debugLog('[🗑️ InsightsService] Archiving insight:', insightId);
    
    const { error } = await supabaseClient
      .from('business_patterns')
      .update({ 
        is_active: false,
        archived_at: new Date().toISOString()
      })
      .eq('id', insightId)
      .eq('user_id', userId);

    if (error) throw error;
    
    debugLog('[✅ InsightsService] Insight archived');
  }

  /**
   * 📤 Export insights en JSON
   */
  static async exportInsights(
    datasetId: string,
    userId: string,
    filters: InsightFilters = {}
  ): Promise<Blob> {
    debugLog('[📤 InsightsService] Exporting insights...');
    
    // Fetch ALL insights matching filters (no pagination)
    const { insights } = await this.getDatasetInsights(
      datasetId,
      userId,
      filters,
      { sortBy: 'discovered_at', sortOrder: 'DESC' },
      0,
      10000 // Max 10K insights per export
    );

    const exportData = {
      dataset_id: datasetId,
      exported_at: new Date().toISOString(),
      total_insights: insights.length,
      filters_applied: filters,
      insights
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    });

    debugLog('[✅ InsightsService] Export ready:', insights.length, 'insights');
    return blob;
  }

  /**
   * 🔄 Refresh materialized views (appeler périodiquement)
   */
  static async refreshMaterializedViews(): Promise<void> {
    debugLog('[🔄 InsightsService] Refreshing materialized views...');
    
    // Appeler via RPC function côté serveur
    const { error } = await supabaseClient.rpc('refresh_bigdata_insights_stats');
    
    if (error) {
      logger.error('[❌ InsightsService] Failed to refresh views:', error);
    } else {
      debugLog('[✅ InsightsService] Views refreshed');
    }
  }
}

export default BigDataInsightsService;

